import MaterialTable from "@material-table/core";
import { EditOffOutlined, EditOutlined, PersonAddOutlined, VisibilityOffOutlined, AdsClickOutlined } from "@mui/icons-material";
import {
    Box,
    Button,
    Card,
    CardContent,
    CardHeader,
    Dialog,
    DialogActions,
    DialogContent,
    DialogTitle,
    FormControl,
    FormControlLabel,
    Switch,
    Table,
    TableBody,
    TableCell,
    TableHead,
    TableRow,
    TextField,
    ToggleButton,
    ToggleButtonGroup,
    Tooltip
} from "@mui/material";
import { get, post } from "aws-amplify/api";
import { fetchAuthSession } from "aws-amplify/auth";
import { Formik } from 'formik';
import { enqueueSnackbar } from "notistack";
import { useContext, useEffect, useState } from "react";
import { object, string } from 'yup';
import { PageAppBar } from "../../components/pageAppBar";
import { UserContext } from "../../contexts/UserContext";
import { actionOneButtonStyle, actionTwoButtonStyle, cardStyle } from "../../theme";

export function Tenants() {
    const userContext = useContext(UserContext);
    const [tenants, setTenants] = useState([]);
    const [loading, setLoading] = useState(false);
    const [newTenantDialogOpen, setNewTenantDialogOpen] = useState(false);
    const [newUserDialogOpen, setNewUserDialogOpen] = useState(false);

    const [newTenant, setNewTenant] = useState({
        tenantId: '',
        licenses: [],
        allowAutoMode: false,
    });

    const [newUser, setNewUser] = useState({
        name: '',
        email: '',
        company: '',
        approver: '0',
        contacts: '0',
        inventoryManagement: '0',
        campaigns: '0',
        communicationProfiles: '0',
        digitalChannels: '0',
        suppression: '0',
        liveAgentSelect: '0',
        connection: '0',
        userManagement: '0'
    });

    const [newUserTenant, setNewUserTenant] = useState('');


    useEffect(() => {
        async function fetchTenants() {
            try {
                setLoading(true);
                const tenantData = await get({
                    apiName: 'cdyxoutreach',
                    path: '/admin/tenant',
                    options: {
                        headers: {
                            Authorization: `Bearer ${(await fetchAuthSession()).tokens.idToken}`,
                            'x-api-key': userContext.apiKey
                        }
                    }
                }).response;

                const tenants = await tenantData.body.json();

                setTenants(tenants);

            } catch (error) {
                console.error("Error fetching tenants:", error);
            }
            setLoading(false);
        }
        if (userContext.id) {
            fetchTenants();
        }
    }, [userContext]);

    async function createTenant() {
        try {
            const response = await post({
                apiName: 'cdyxoutreach',
                path: '/admin/tenant',
                options: {
                    body: newTenant,
                    headers: {
                        Authorization: `Bearer ${(await fetchAuthSession()).tokens.idToken}`,
                        'x-api-key': userContext.apiKey
                    }
                }
            }).response;
            const result = await response.body.json();
            console.log("Tenant created:", result);
            setTenants([...tenants, result]);
            setNewTenantDialogOpen(false);
            setNewTenant({
                tenantId: '',
                licenses: [],
                allowAutoMode: false,
            });
        } catch (error) {
            console.error("Error creating tenant:", error);
        }
    }

    return (
        <>
            <PageAppBar
                title="Tenants"
                actionOneText="+ Tenant"
                actionOneHandler={() => setNewTenantDialogOpen(true)}
            />
            <Card variant="outlined" sx={cardStyle}>
                <CardContent>
                    <MaterialTable
                        components={{
                            Container: (props) => <Box {...props} elevation={0} />,
                        }}
                        title=""
                        data={tenants}
                        columns={[{ title: 'Tenant', field: 'id' },
                        { title: 'API Key', field: 'apiKey' },
                        {
                            title: 'Telephony Provider', field: 'telephonyProvider',
                            render: (rowData) => {
                                return rowData.telephonyProvider ? rowData.telephonyProvider : 'None';
                            }
                        }
                        ]}
                        isLoading={loading}
                        options={{
                            actionsColumnIndex: -1,
                            pageSize: 10,
                            searchFieldStyle: {
                                marginBottom: '16px',
                                marginleft: '-28px',
                            },
                            headerStyle: {
                                borderTop: '1px solid #e0e0e0',
                                padding: '16px',
                            },
                            searchFieldVariant: 'outlined',
                            paginationType: 'stepped',
                        }}
                        actions={[
                            {
                                icon: () => <PersonAddOutlined color="primary" />,
                                tooltip: 'Add Tenant User',
                                onClick: (event, rowData) => {
                                    setNewUserDialogOpen(true);
                                    setNewUserTenant(rowData.id);
                                }
                            },
                        ]}
                    />
                </CardContent>
            </Card>

            <Dialog open={newTenantDialogOpen} onClose={() => setNewTenantDialogOpen(false)}>
                <DialogTitle>New Tenant</DialogTitle>
                <DialogContent>
                    <Box display="flex" flexDirection="column" gap={2} sx={{ marginTop: '1em' }}>
                        <TextField
                            label="Tenant ID"
                            variant="outlined"
                            value={newTenant.tenantId}
                            onChange={(e) => setNewTenant({ ...newTenant, tenantId: e.target.value })}
                        />
                        <FormControl>
                            <FormControlLabel
                                control={
                                    <Switch
                                        checked={newTenant.allowAutoMode}
                                        onChange={(e) => setNewTenant({ ...newTenant, allowAutoMode: e.target.checked })}
                                    />
                                }
                                label="Allow Auto Mode"
                            />
                        </FormControl>
                    </Box>
                </DialogContent>
                <DialogActions>
                    <Button sx={actionTwoButtonStyle} onClick={() => setNewTenantDialogOpen(false)}>Cancel</Button>
                    <Button sx={actionOneButtonStyle} onClick={createTenant}>Create</Button>

                </DialogActions>
            </Dialog>

            <Dialog open={newUserDialogOpen} maxWidth="xl" fullWidth onClose={() => { setNewUserDialogOpen(false) }}>
                <DialogTitle>{`Create User for ${newUserTenant}`}</DialogTitle>
                <DialogContent>
                    <Formik
                        initialValues={newUser}
                        onSubmit={async values => {
                            console.log('Save user', values);
                            try {
                                await post({
                                    apiName: 'cdyxoutreach',
                                    path: '/admin/user',
                                    options: {
                                        body: {
                                            email: values.email,
                                            name: values.name,
                                            tenant: newUserTenant,
                                            password: values.password,
                                            tenantRole: 'User',
                                            securityOptions: `${values.contacts}|${values.inventoryManagement}|${values.campaigns}|${values.communicationProfiles}|${values.digitalChannels}|${values.suppression}|${values.liveAgentSelect}|${values.connection}|${values.userManagement}`,
                                            company: values.company,
                                            approver: values.approver === '1' ? '1' : '0'
                                        },
                                        headers: {
                                            Authorization: `Bearer ${(await fetchAuthSession()).tokens.idToken}`,
                                            'x-api-key': userContext.apiKey,
                                            'Content-Type': 'application/json',
                                        }
                                    }
                                }).response;

                                enqueueSnackbar(`User ${values.name} created for tenant ${newUserTenant}`, { variant: 'success' });
                                setNewUserDialogOpen(false);
                                setNewUser({
                                    name: '',
                                    email: '',
                                    company: '',
                                    approver: '0',
                                    contacts: '0',
                                    inventoryManagement: '0',
                                    campaigns: '0',
                                    communicationProfiles: '0',
                                    digitalChannels: '0',
                                    suppression: '0',
                                    liveAgentSelect: '0',
                                    connection: '0',
                                    userManagement: '0'
                                });
                                setNewUserTenant('');

                            } catch (err) {
                                console.log('Error creating user', err)
                                enqueueSnackbar('Error creating user', { variant: 'error' });
                            }
                        }

                        }
                        enableReinitialize
                        validationSchema={
                            object().shape({
                                name: string().required('Name is required'),
                                email: string().email('Invalid email').required('Email is required'),
                                company: string().required('Company is required'),
                                approver: string().required('Approver is required'),
                                contacts: string().required('Contacts is required'),
                                inventoryManagement: string().required('Inventory Management is required'),
                                campaigns: string().required('Campaigns is required'),
                                communicationProfiles: string().required('Communication Profiles is required'),
                                digitalChannels: string().required('Digital Channels is required'),
                                suppression: string().required('Suppression is required'),
                                liveAgentSelect: string().required('Live Agent Select is required'),
                                connection: string().required('Connection is required'),
                                userManagement: string().required('User Management is required'),
                                password: string().required('Password is required')
                            })
                        }
                    >
                        {props => {
                            const {
                                values,
                                touched,
                                errors,
                                handleChange,
                                handleBlur,
                                handleSubmit,
                                isSubmitting,
                                setFieldValue
                            } = props;
                            return (
                                <form onSubmit={handleSubmit}>
                                    <Box display="flex" flexDirection="column" gap={2}>
                                        <Box display="grid" gridTemplateColumns={newUser ? "1fr 1fr 1fr 1fr" : "1fr 1fr 1fr"} gap={2} sx={{ marginTop: 2 }}>
                                            <TextField
                                                id="name"
                                                label="Name"
                                                value={values.name}
                                                onChange={handleChange}
                                                onBlur={handleBlur}
                                                error={errors.name && touched.name}
                                                helperText={(errors.name && touched.name) && errors.name}
                                            />
                                            <TextField
                                                id="email"
                                                label="Email"
                                                value={values.email}
                                                onChange={handleChange}
                                                onBlur={handleBlur}
                                                error={errors.email && touched.email}
                                                helperText={(errors.email && touched.email) && errors.email}
                                            />
                                            <TextField
                                                id="company"
                                                label="Company"
                                                value={values.company}
                                                onChange={handleChange}
                                                onBlur={handleBlur}
                                                error={errors.company && touched.company}
                                                helperText={(errors.company && touched.company) && errors.company}
                                            />
                                            {newUser &&
                                                <TextField
                                                    id="password"
                                                    label="Password"
                                                    value={values.password}
                                                    onChange={handleChange}
                                                    onBlur={handleBlur}
                                                    error={errors.password && touched.password}
                                                    helperText={(errors.password && touched.password) && errors.password}
                                                />}
                                        </Box>
                                        <Card variant="outlined" sx={cardStyle}>
                                            <CardHeader title="Security Rights" titleTypographyProps={{ variant: 'body2' }} />
                                            <CardContent>
                                                <Table>
                                                    <TableHead>
                                                        <TableRow>
                                                            <TableCell>Contacts</TableCell>
                                                            <TableCell>Inventory Management</TableCell>
                                                            <TableCell>Campaigns</TableCell>
                                                            <TableCell>Communication Profiles</TableCell>
                                                            <TableCell>Digital Channels</TableCell>
                                                        </TableRow>
                                                    </TableHead>
                                                    <TableBody>
                                                        <TableRow>
                                                            <TableCell>
                                                                <ToggleButtonGroup value={values.contacts} exclusive onChange={(e, value) => { setFieldValue('contacts', value) }}>
                                                                    <Tooltip title="No Access">
                                                                        <ToggleButton value="0">
                                                                            <VisibilityOffOutlined color="primary" />
                                                                        </ToggleButton>
                                                                    </Tooltip>
                                                                    <Tooltip title="Read Only">
                                                                        <ToggleButton value="1">
                                                                            <EditOffOutlined color="primary" />
                                                                        </ToggleButton>
                                                                    </Tooltip>
                                                                    <Tooltip title="Read/Write">
                                                                        <ToggleButton value="2">
                                                                            <EditOutlined color="primary" />
                                                                        </ToggleButton>
                                                                    </Tooltip>
                                                                </ToggleButtonGroup>
                                                            </TableCell>
                                                            <TableCell>
                                                                <ToggleButtonGroup value={values.inventoryManagement} exclusive onChange={(e, value) => { setFieldValue('inventoryManagement', value) }}>
                                                                    <Tooltip title="No Access">
                                                                        <ToggleButton value="0">
                                                                            <VisibilityOffOutlined color="primary" />
                                                                        </ToggleButton>
                                                                    </Tooltip>
                                                                    <Tooltip title="Read Only">
                                                                        <ToggleButton value="1">
                                                                            <EditOffOutlined color="primary" />
                                                                        </ToggleButton>
                                                                    </Tooltip>
                                                                    <Tooltip title="Read/Write">
                                                                        <ToggleButton value="2">
                                                                            <EditOutlined color="primary" />
                                                                        </ToggleButton>
                                                                    </Tooltip>
                                                                </ToggleButtonGroup>
                                                            </TableCell>
                                                            <TableCell>
                                                                <ToggleButtonGroup value={values.campaigns} exclusive onChange={(e, value) => { setFieldValue('campaigns', value) }}>
                                                                    <Tooltip title="No Access">
                                                                        <ToggleButton value="0">
                                                                            <VisibilityOffOutlined color="primary" />
                                                                        </ToggleButton>
                                                                    </Tooltip>
                                                                    <Tooltip title="Read Only">
                                                                        <ToggleButton value="1">
                                                                            <EditOffOutlined color="primary" />
                                                                        </ToggleButton>
                                                                    </Tooltip>
                                                                    <Tooltip title="Read/Write">
                                                                        <ToggleButton value="2">
                                                                            <EditOutlined color="primary" />
                                                                        </ToggleButton>
                                                                    </Tooltip>
                                                                </ToggleButtonGroup>
                                                            </TableCell>
                                                            <TableCell>
                                                                <ToggleButtonGroup value={values.communicationProfiles} exclusive onChange={(e, value) => { setFieldValue('communicationProfiles', value) }}>
                                                                    <Tooltip title="No Access">
                                                                        <ToggleButton value="0">
                                                                            <VisibilityOffOutlined color="primary" />
                                                                        </ToggleButton>
                                                                    </Tooltip>
                                                                    <Tooltip title="Read Only">
                                                                        <ToggleButton value="1">
                                                                            <EditOffOutlined color="primary" />
                                                                        </ToggleButton>
                                                                    </Tooltip>
                                                                    <Tooltip title="Read/Write">
                                                                        <ToggleButton value="2">
                                                                            <EditOutlined color="primary" />
                                                                        </ToggleButton>
                                                                    </Tooltip>
                                                                </ToggleButtonGroup>
                                                            </TableCell>
                                                            <TableCell>
                                                                <ToggleButtonGroup value={values.digitalChannels} exclusive onChange={(e, value) => { setFieldValue('digitalChannels', value) }}>
                                                                    <Tooltip title="No Access">
                                                                        <ToggleButton value="0">
                                                                            <VisibilityOffOutlined color="primary" />
                                                                        </ToggleButton>
                                                                    </Tooltip>
                                                                    <Tooltip title="Read Only">
                                                                        <ToggleButton value="1">
                                                                            <EditOffOutlined color="primary" />
                                                                        </ToggleButton>
                                                                    </Tooltip>
                                                                    <Tooltip title="Read/Write">
                                                                        <ToggleButton value="2">
                                                                            <EditOutlined color="primary" />
                                                                        </ToggleButton>
                                                                    </Tooltip>
                                                                </ToggleButtonGroup>
                                                            </TableCell>
                                                        </TableRow>
                                                    </TableBody>
                                                </Table>
                                                <Table>
                                                    <TableHead>
                                                        <TableRow>

                                                            <TableCell>Suppression</TableCell>
                                                            <TableCell>Live Agent Select</TableCell>
                                                            <TableCell>Connection</TableCell>
                                                            <TableCell>User Management</TableCell>

                                                        </TableRow>
                                                    </TableHead>
                                                    <TableBody>
                                                        <TableRow>

                                                            <TableCell>
                                                                <ToggleButtonGroup value={values.suppression} exclusive onChange={(e, value) => { setFieldValue('suppression', value) }}>
                                                                    <Tooltip title="No Access">
                                                                        <ToggleButton value="0">
                                                                            <VisibilityOffOutlined color="primary" />
                                                                        </ToggleButton>
                                                                    </Tooltip>
                                                                    <Tooltip title="Read Only">
                                                                        <ToggleButton value="1">
                                                                            <EditOffOutlined color="primary" />
                                                                        </ToggleButton>
                                                                    </Tooltip>
                                                                    <Tooltip title="Read/Write">
                                                                        <ToggleButton value="2">
                                                                            <EditOutlined color="primary" />
                                                                        </ToggleButton>
                                                                    </Tooltip>
                                                                </ToggleButtonGroup>
                                                            </TableCell>
                                                            <TableCell>
                                                                <ToggleButtonGroup value={values.liveAgentSelect} exclusive onChange={(e, value) => {
                                                                    setFieldValue('liveAgentSelect', value)
                                                                    if (value > 0) {
                                                                        setFieldValue('approver', '1')
                                                                    } else {
                                                                        setFieldValue('approver', '0')
                                                                    }
                                                                }}>
                                                                    <Tooltip title="No Access">
                                                                        <ToggleButton value="0">
                                                                            <VisibilityOffOutlined color="primary" />
                                                                        </ToggleButton>
                                                                    </Tooltip>
                                                                    <Tooltip title="Approver">
                                                                        <ToggleButton value="2">
                                                                            <AdsClickOutlined color="primary" />
                                                                        </ToggleButton>
                                                                    </Tooltip>
                                                                </ToggleButtonGroup>
                                                            </TableCell>
                                                            <TableCell>
                                                                <ToggleButtonGroup value={values.connection} exclusive onChange={(e, value) => { setFieldValue('connection', value) }}>
                                                                    <Tooltip title="No Access">
                                                                        <ToggleButton value="0">
                                                                            <VisibilityOffOutlined color="primary" />
                                                                        </ToggleButton>
                                                                    </Tooltip>
                                                                    <Tooltip title="Read Only">
                                                                        <ToggleButton value="1">
                                                                            <EditOffOutlined color="primary" />
                                                                        </ToggleButton>
                                                                    </Tooltip>
                                                                    <Tooltip title="Read/Write">
                                                                        <ToggleButton value="2">
                                                                            <EditOutlined color="primary" />
                                                                        </ToggleButton>
                                                                    </Tooltip>
                                                                </ToggleButtonGroup>
                                                            </TableCell>
                                                            <TableCell>
                                                                <ToggleButtonGroup value={values.userManagement} exclusive onChange={(e, value) => { setFieldValue('userManagement', value) }}>
                                                                    <Tooltip title="No Access">
                                                                        <ToggleButton value="0">
                                                                            <VisibilityOffOutlined color="primary" />
                                                                        </ToggleButton>
                                                                    </Tooltip>
                                                                    <Tooltip title="Read Only">
                                                                        <ToggleButton value="1">
                                                                            <EditOffOutlined color="primary" />
                                                                        </ToggleButton>
                                                                    </Tooltip>
                                                                    <Tooltip title="Read/Write">
                                                                        <ToggleButton value="2">
                                                                            <EditOutlined color="primary" />
                                                                        </ToggleButton>
                                                                    </Tooltip>
                                                                </ToggleButtonGroup>
                                                            </TableCell>
                                                        </TableRow>
                                                    </TableBody>
                                                </Table>

                                            </CardContent>
                                        </Card>
                                    </Box>
                                    <DialogActions>
                                        <Button sx={actionTwoButtonStyle} onClick={() => {
                                            setNewUserDialogOpen(false);
                                            setNewUser({
                                                name: '',
                                                email: '',
                                                company: '',
                                                approver: '0',
                                                contacts: '0',
                                                inventoryManagement: '0',
                                                campaigns: '0',
                                                communicationProfiles: '0',
                                                digitalChannels: '0',
                                                suppression: '0',
                                                liveAgentSelect: '0',
                                                connection: '0',
                                                userManagement: '0'
                                            });
                                            setNewUserTenant('');

                                        }}>Cancel</Button>

                                        <Button sx={actionOneButtonStyle} type="submit">Save</Button>

                                    </DialogActions>
                                </form>
                            )
                        }}
                    </Formik>
                </DialogContent>
            </Dialog>
        </>)
}